/**
=========================================================
* Vue SEVICA - v3.0.0
=========================================================
* Product Page: https://picko.sevica.es
* Copyright 2023 Sevica (https://www.sevica.es)
Coded by mnavarro
=========================================================
* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { createApp } from "vue";
import App from "./App.vue";
import store from "./store";
import router from "./router";
import "./assets/css/nucleo-icons.css";
import "./assets/css/nucleo-svg.css";
import VueTilt from "vue-tilt.js";
import VueSweetalert2 from "vue-sweetalert2";
import SoftUIDashboard from "./soft-ui-dashboard";
import hotaka from "./hotaka/js/hotaka-ui.js";
import { createI18n } from 'vue-i18n';
import Cookies from 'js-cookie';
import Notification from './plugins/Notification.js';
import Permission from './plugins/Permission.js';
import PickoVersion from './plugins/PickoVersion.js';
import { startCrataWidget } from './plugins/CrataWidget.js';

const savedLanguage = Cookies.get('Picko.language') || 'en';

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: savedLanguage,
  fallbackLocale: 'en',
  messages: {}
});

// Estado para evitar múltiples cargas simultáneas
let currentLoadingLocale = null;

// Función para cargar las traducciones asíncronamente
async function loadLocaleMessages(locale) {
  if (currentLoadingLocale === locale) {
    return;
  }
  
  currentLoadingLocale = locale;

  try {
    // Cargar las traducciones del idioma requerido
    const messages = await import(`./locales/${locale}.json`);

    i18n.global.setLocaleMessage(locale, messages.default);
  } catch (error) {
    console.error(`Error al cargar las traducciones para el idioma: ${locale}`, error);
  } finally {
    // eslint-disable-next-line require-atomic-updates
    currentLoadingLocale = null;
  }
}

async function setLanguage(locale) {
  if (i18n.global.locale.value === locale) {
    return;
  }
  
  // Si el idioma no está disponible, lo cargamos
  if (!i18n.global.availableLocales.includes(locale)) {
    await loadLocaleMessages(locale);
  }

  // eslint-disable-next-line require-atomic-updates
  i18n.global.locale.value = locale;
  Cookies.set('Picko.language', locale, { expires: 365 });
}

(async () => {
  await loadLocaleMessages(savedLanguage);
  i18n.global.locale.value = savedLanguage;

  const appInstance = createApp(App);
  appInstance.use(store);
  appInstance.use(router);
  appInstance.use(VueTilt);
  appInstance.use(VueSweetalert2);
  appInstance.use(SoftUIDashboard);
  appInstance.use(i18n);
  appInstance.use(Notification);
  appInstance.use(PickoVersion);
  Permission.init();
  appInstance.config.globalProperties.$Permission = Permission;

  hotaka.configureApp(appInstance);

  // Clarify injection
  if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_CLARITY_ID) {
    (function(c, l, a, r, i, t, y) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments) };
      t = l.createElement(r); t.async = 1; t.src = "https://www.clarity.ms/tag/" + i;
      y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
    })(window, document, "clarity", "script", process.env.VUE_APP_CLARITY_ID);
  }

  appInstance.mount("#app");
  startCrataWidget();
})();

export { i18n, setLanguage };